import cn from "classnames";
import { payableStatuses, paymentFieldsConfig } from "components/distributionBids/paymentFields";
import { Datetime, Field } from "components/shared/form";
import { bidPaymentStatuses, bidPaymentStatusesEnum } from "helpers/enums";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { Popover } from "react-tiny-popover";
import { applyReportPayment } from "./payment";

export default function WithChangeStatus({
    payment: initialPayment,
    updateData,
    children,
    isAccountsPayable,
    disabled,
}) {
    const [opened, setOpened] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [payment, setPayment] = useState(initialPayment ?? {});
    const paymentConfig = paymentFieldsConfig(isAccountsPayable);
    useEffect(() => {
        setPayment(initialPayment ?? {});
    }, [initialPayment]);

    const handleChange = useCallback(({ name, value }) => {
        if (["factPaymentDate"].includes(name) && value) {
            value = moment(value).format("YYYY-MM-DD");
        }
        const newValue = { [name]: value };
        if (name === "bidPaymentStatus") {
            if (value !== bidPaymentStatusesEnum.PartiallyPaid) {
                newValue.remainingPayment = null;
            }
            if (value !== bidPaymentStatusesEnum.Paid) {
                newValue.factPaymentDate = null;
            }
        }
        setPayment((prev) => ({ ...prev, ...newValue }));
        setError("");
    }, []);

    const savePayment = useCallback(() => {
        if (payment.bidPaymentStatus !== bidPaymentStatusesEnum.NotPaid && !payment.planPaymentDate) {
            setError(`Укажите ${paymentConfig.planPaymentDate.label.toLowerCase()}`);
            return;
        }
        if (payment.bidPaymentStatus === bidPaymentStatusesEnum.Paid && !payment.factPaymentDate) {
            setError(`Укажите ${paymentConfig.factPaymentDate.label.toLowerCase()}`);
            return;
        }
        if (
            payment.bidPaymentStatus === bidPaymentStatusesEnum.PartiallyPaid &&
            (payment.remainingPayment === null ||
                payment.remainingPayment === undefined ||
                payment.remainingPayment === "")
        ) {
            setError(`Укажите ${paymentConfig.remainingPayment.label.toLowerCase()}`);
            return;
        }
        if (loading || disabled) {
            return;
        }
        setLoading(true);
        const {
            invoiceDate,
            planPaymentDate,
            factPaymentDate,
            bidPaymentStatus: paymentStatus,
            comment,
            remainingPayment,
        } = payment;
        const newPaymentData = {
            comment,
            paymentStatus,
            invoiceDate,
            planPaymentDate,
            factPaymentDate,
            remainingPayment,
        };
        applyReportPayment(payment, newPaymentData, isAccountsPayable)
            .then((response) => {
                if (response) {
                    setOpened(false);
                    updateData({
                        ...payment,
                        ...newPaymentData,
                        bidPaymentStatus: paymentStatus,
                    });
                }
                if (!response) {
                    setError("Не удалось сохранить изменения. Повторите запрос.");
                }
                setLoading(false);
            })
            .catch(() => {
                setError("Не удалось сохранить изменения. Повторите запрос.");
                setLoading(false);
            });
    }, [
        isAccountsPayable,
        payment,
        paymentConfig.remainingPayment.label,
        paymentConfig.planPaymentDate.label,
        paymentConfig.factPaymentDate.label,
        updateData,
        loading,
        disabled,
    ]);

    const handlePopoverToggle = useCallback(() => setOpened((open) => !open), []);

    const handlePopoverClose = useCallback(() => {
        setOpened(false);
        setPayment(initialPayment);
        setError("");
    }, [initialPayment]);

    const popoverContent = (
        <div className={cn("popover w-auto font-size-14", { "loading loading--small": loading })}>
            <div className="font-size-16 pb-2">Изменение данных об оплате</div>
            <div>
                <div className="form-group">
                    <label className="small mb-0" htmlFor={`payment-status-${payment.bidId}`}>
                        {paymentConfig.paymentStatus.label}
                    </label>
                    <select
                        id={`payment-status-${payment.bidId}`}
                        className="form-control form-control-xs"
                        onChange={(e) => handleChange({ name: "bidPaymentStatus", value: e.target.value })}
                        value={payment.bidPaymentStatus}
                    >
                        {(isAccountsPayable ? payableStatuses : bidPaymentStatuses).map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                </div>
                {payment.bidPaymentStatus === bidPaymentStatusesEnum.Paid && (
                    <Datetime
                        labelWrapper="col-12 small mb-0"
                        wrapper="col-12"
                        inPortal
                        {...paymentConfig.factPaymentDate}
                        libOptions={{ className: "form-control form-control-xs" }}
                        onChange={handleChange}
                        value={payment.factPaymentDate}
                    />
                )}
                {payment.bidPaymentStatus === bidPaymentStatusesEnum.PartiallyPaid && (
                    <Field
                        labelWrapper="col-12 small mb-0"
                        fieldWrapper="col-12"
                        {...paymentConfig.remainingPayment}
                        type="number"
                        onChange={handleChange}
                        value={payment.remainingPayment ?? ""}
                    />
                )}
                {error && <div className="text-danger small mb-2">{error}</div>}
                <div className="d-flex justify-content-between mb-2">
                    <button type="button" className="btn btn-xs btn-secondary" onClick={handlePopoverClose}>
                        Отмена
                    </button>
                    <button
                        type="button"
                        className="btn btn-xs btn-brand"
                        onClick={savePayment}
                        disabled={loading || disabled}
                    >
                        Сохранить
                    </button>
                </div>
            </div>
        </div>
    );

    const onClickOutside = useCallback(
        (e) => {
            if (opened && !e.target.closest(".react-datepicker")) {
                handlePopoverClose();
            }
        },
        [opened, handlePopoverClose],
    );

    return (
        <Popover
            isOpen={opened}
            content={popoverContent}
            containerStyle={{ zIndex: 130 }}
            onClickOutside={onClickOutside}
        >
            <button
                type="button"
                className="r-item__with-date border-0 bg-transparent text-reset w-100"
                onClick={handlePopoverToggle}
                disabled={disabled || loading}
            >
                <div className="flex-grow-1 text-center">
                    <div>{children}</div>
                </div>
                {!disabled && <i className="icon-uEA10-pen px-2" />}
            </button>
        </Popover>
    );
}
